/**
 * **层级限定的节点 id**（`group_x/const_y` 这种）—— 解析与拼装。
 *
 * ## 为什么需要它
 *
 * 子图（组 / 循环）里求值时，节点的 id 会带上**层级路径**，这样它在整份文档里
 * 才是唯一的（同一个组被摆两次，内部节点 id 会撞）。
 *
 * 但**画布与活动节点用的是"当前层"的纯 id** —— 于是任何"从别处拿一个 id
 * 去定位"的通道（运行记录、诊断、输出窗口）都必须先把路径剥掉，
 * 否则 `activeNodeId` 会被设成一个**不存在的 id**：高亮没有、属性面板空着、
 * 镜头不动。表现就是**点了没反应**（这条在"运行记录 → 定位"上踩过一次，
 * 查了两轮才定位到层级这一层）。
 *
 * ⚠️ 归 `graph/` 而不是 `node/`：这是**图**的 id 约定（层的概念在图里），
 * 节点层不认识"层"。
 */

export interface LevelQualifiedId {
  /** 从最外层到最内层的**层**（每一段是那一层里容纳子图的那个节点 id） */
  levels: string[]
  /** 最后一层里的**纯节点 id** */
  nodeId: string
}

/** 拆开一个层级限定的 id（没有 `/` 时 `levels` 为空） */
export function splitLevelId(qualified: string): LevelQualifiedId {
  const parts = qualified.split('/').filter((part) => part !== '')
  if (parts.length === 0) return { levels: [], nodeId: '' }
  const nodeId = parts[parts.length - 1] ?? ''
  return { levels: parts.slice(0, -1), nodeId }
}

/** 拼回去（缺哪一段就少哪一段；`levels` 为空时就是纯 id） */
export function joinLevelId(levels: readonly string[], nodeId: string): string {
  return [...levels.filter((level) => level !== ''), nodeId].filter((part) => part !== '').join('/')
}
